import React from "react";
import "./ValidationErrorsPopover.scss";
import { DomainValidationError } from "@/types/FinancialApplicationValidationState";
import { faArrowRight, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FinancialApplicationSection } from "@/types/enums";
import { APPLICATION_NAVIGATION_ITEMS } from "@/types/ApplicationNavigationItems";

interface ValidationErrorsPopoverProps {
  /** List of validation errors to display */
  errors: Array<DomainValidationError>;
  /** Label to display in the popover header */
  popoverLabel?: string;
  /** Label to display before the count */
  label?: string;
  /** Function to get display name for a property name */
  getPropertyDisplayName?: (error: DomainValidationError) => string;
  /** An optional function that when provided will navigate to the field that has the error. */
  navigateToField?: (error: DomainValidationError) => Promise<void> | void; 
  /** Whether to show the section title in the popover. */
  showSectionTitle?: boolean;
  /** The total number of required fields. */
  totalRequiredFieldCount?: number;
}

/**
 * ValidationErrorsPopover displays a detailed list of validation errors
 * with visual distinctions between required and non-required errors.
 */
export const ValidationErrorsPopover: React.FC<ValidationErrorsPopoverProps> = ({
  errors,
  popoverLabel,
  label,
  getPropertyDisplayName,
  navigateToField,
  showSectionTitle,
  totalRequiredFieldCount,
}) => {
  const displayLabel = popoverLabel || label || "Validation Errors";
  
  // Group errors by type for better organization
  const requiredErrors = errors.filter(error => error.isRequiredErrorCode);
  const nonRequiredErrors = errors.filter(error => !error.isRequiredErrorCode);
  
  const getSectionName = (e: DomainValidationError) => {
      const sectionsCsv = e.customStateDictionary?.appliesToBankerSections;
 
      if (sectionsCsv) {
        const sections = sectionsCsv.split(",").map(Number);
        const firstSection = sections[0] as FinancialApplicationSection;

        // The firstSection is a number that applies to the enum of the section of APPLICATION_NAVIGATION_ITEMS
        // Get label
        const section = APPLICATION_NAVIGATION_ITEMS[firstSection];
        return section?.label;
      }
      return "Unknown Section";
  };

  const getDisplayName = (error: DomainValidationError) => {
    const defaultDisplayName = error.displayName || error.propertyName || "Unknown Field";
    return getPropertyDisplayName?.(error) || defaultDisplayName;
  };

  const handleNavigateToField = (error: DomainValidationError) => {
    if (navigateToField) {
      navigateToField(error);
    }
  };

  const renderErrorList = (errorList: typeof errors, title: string, errorClass: string) => {
    if (errorList.length === 0) return null;

    return (
      <div className={`validation-errors-popover__section ${errorClass}`}>
        <h4 className={`validation-errors-popover__section-title`}>
          {title} ({errorList.length})
        </h4>
        <ul className="validation-errors-popover__error-list">
          {errorList.map((error, index) => (
            <li key={index} className={classNames(`validation-errors-popover__error-item`, {
              "validation-errors-popover__error-item--navigate-to-field": !!navigateToField
            })} onClick={() => {
              !!navigateToField && handleNavigateToField(error);
            }}>
              <div className="validation-errors-popover__error-content" >
                {showSectionTitle && (
                  <>
                  <div className="validation-errors-popover__error-section">
                    {getSectionName(error)}
                  </div>
                   <FontAwesomeIcon icon={faChevronRight} size="xs" className="validation-errors-popover__error-section-icon" />
                  </>
                )}         
                <div className="validation-errors-popover__error-field">
                  {getDisplayName(error)}
                </div>
                {error.errorMessage && (
                  <div className="validation-errors-popover__error-message">
                    {error.errorMessage}
                  </div>
                )}
              </div>
              {navigateToField && (
                <div className="validation-errors-popover__error-action">
                  <FontAwesomeIcon icon={faArrowRight} className="validation-errors-popover__error-action-icon" />
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="validation-errors-popover">
      <div className="validation-errors-popover__header">
        <h3 className="validation-errors-popover__title">{displayLabel}</h3>
        <h3 className="validation-errors-popover__title-count">{errors.length} {!!totalRequiredFieldCount ? `/ ${totalRequiredFieldCount}` : ""}</h3>
      </div>
      
      <div className="validation-errors-popover__content">
        {renderErrorList(requiredErrors, "Required Fields", "validation-errors-popover__section--required")}
        {renderErrorList(nonRequiredErrors, "Other Issues", "validation-errors-popover__section--non-required")}
        
        {errors.length === 0 && (
          <div className="validation-errors-popover__no-errors">
            No validation errors found.
          </div>
        )}
      </div>
    </div>
  );
};
